import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { documentService } from '../../services/document.service';
import type { DrawingEvidence, FailureMode } from '../../types/drbfm';

interface DRBFMDrawingEvidenceDialogProps {
  open: boolean;
  failureMode: FailureMode | null;
  onClose: () => void;
}

interface DocumentImageSummary {
  id: string;
  pageNumber: number;
  imageType: string;
}

const IMAGE_TYPES_BY_PRIORITY = ['full_page', 'blueprint'];

const clamp = (value: number, min: number, max: number): number => (
  Math.max(min, Math.min(max, value))
);

const toPercent = (value: number): string => `${Number((value * 100).toFixed(1))}%`;

const buildDocumentUrl = (evidence: DrawingEvidence): string => {
  const base = `/documents/${encodeURIComponent(evidence.documentId)}`;
  return evidence.pageNumber ? `${base}?page=${evidence.pageNumber}` : base;
};

const selectPageImage = (
  images: DocumentImageSummary[],
  pageNumber?: number,
): DocumentImageSummary | undefined => {
  const pageImages = pageNumber
    ? images.filter((image) => image.pageNumber === pageNumber)
    : images;
  return pageImages.find((image) => IMAGE_TYPES_BY_PRIORITY.includes(image.imageType))
    || pageImages[0];
};

const getHighlightStyle = (evidence: DrawingEvidence): React.CSSProperties | null => {
  if (evidence.bbox.unit !== 'normalized') {
    return null;
  }

  const left = clamp(evidence.bbox.x, 0, 1);
  const top = clamp(evidence.bbox.y, 0, 1);
  const width = clamp(evidence.bbox.width, 0, 1 - left);
  const height = clamp(evidence.bbox.height, 0, 1 - top);

  if (width <= 0 || height <= 0) {
    return null;
  }

  return {
    position: 'absolute',
    left: toPercent(left),
    top: toPercent(top),
    width: toPercent(width),
    height: toPercent(height),
    border: '3px solid #d32f2f',
    boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.2)',
    pointerEvents: 'none',
  };
};

const DRBFMDrawingEvidenceDialog: React.FC<DRBFMDrawingEvidenceDialogProps> = ({
  open,
  failureMode,
  onClose,
}) => {
  const { t } = useTranslation();
  const evidence = failureMode?.drawingEvidence;
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fallbackUrl = evidence ? buildDocumentUrl(evidence) : undefined;
  const highlightStyle = useMemo(
    () => (evidence ? getHighlightStyle(evidence) : null),
    [evidence],
  );

  useEffect(() => {
    if (!open || !evidence) {
      setImageUrl(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrlToRevoke: string | null = null;

    const loadEvidenceImage = async () => {
      if (!evidence.documentId) {
        setError(t('drbfm.drawingEvidence.missingDocumentId', '図面根拠の文書IDがありません。'));
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setImageUrl(null);

        let imageId = evidence.imageId;
        if (!imageId) {
          const imageData = await documentService.getDocumentImages(evidence.documentId);
          const selectedImage = selectPageImage(imageData.images || [], evidence.pageNumber);
          imageId = selectedImage?.id;
        }

        if (!imageId) {
          throw new Error(t('drbfm.drawingEvidence.imageNotFound', '対象ページの画像が見つかりません。'));
        }

        const blobUrl = await documentService.getDocumentImageBlob(evidence.documentId, imageId);
        objectUrlToRevoke = blobUrl;
        if (!cancelled) {
          setImageUrl(blobUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('drbfm.drawingEvidence.imageLoadFailed', '図面画像を読み込めませんでした。'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadEvidenceImage();

    return () => {
      cancelled = true;
      if (
        objectUrlToRevoke
        && objectUrlToRevoke.startsWith('blob:')
        && typeof URL !== 'undefined'
        && typeof URL.revokeObjectURL === 'function'
      ) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds error messages; excluded so a language change doesn't refetch the drawing image (PR #790)
  }, [evidence, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {evidence?.label || t('drbfm.drawingEvidence.title', '図面根拠')}
      </DialogTitle>
      <DialogContent dividers>
        {!evidence && (
          <Alert severity="info">{t('drbfm.drawingEvidence.notLinked', '図面根拠はまだ紐付いていません。')}</Alert>
        )}

        {evidence && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {evidence.reasoning || failureMode?.failureDescription}
              </Typography>
              {fallbackUrl && (
                <Link href={fallbackUrl} target="_blank" rel="noreferrer" underline="hover">
                  {t('drbfm.drawingEvidence.openDocumentPage', '文書ページを開く')}
                </Link>
              )}
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert
                severity="warning"
                action={fallbackUrl ? (
                  <Button
                    color="inherit"
                    size="small"
                    href={fallbackUrl}
                    target="_blank"
                    rel="noreferrer"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                  >
                    {t('drbfm.drawingEvidence.goToDocument', '文書へ')}
                  </Button>
                ) : undefined}
              >
                {error}
              </Alert>
            )}

            {imageUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxHeight: '72vh',
                  overflow: 'auto',
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-block', minWidth: '100%' }}>
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={evidence.label}
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                    }}
                  />
                  {highlightStyle && (
                    <Box
                      data-testid="drawing-evidence-highlight"
                      sx={highlightStyle}
                    />
                  )}
                </Box>
              </Box>
            )}

            {imageUrl && !highlightStyle && (
              <Alert severity="info">
                {t('drbfm.drawingEvidence.missingNormalizedBbox', '正規化されたbboxがないため、文書ページで根拠箇所を確認してください。')}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', '閉じる')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DRBFMDrawingEvidenceDialog;
